import React, { useState, useEffect } from 'react';
import { useAuth } from '../hooks/useAuth';
import Calendar from './Calendar';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ClinicDashboard = () => {
  const [activeTab, setActiveTab] = useState('appointments');
  const [appointments, setAppointments] = useState([]);
  const [selectedDate, setSelectedDate] = useState('');
  const [availableSlots, setAvailableSlots] = useState({});
  const [bookedSlots, setBookedSlots] = useState({});
  const [loading, setLoading] = useState(true);
  const [clinicInfo, setClinicInfo] = useState(null);
  const { user } = useAuth();

  useEffect(() => {
    if (user) {
      fetchClinicInfo();
      fetchAppointments();
      fetchAvailability();
    }
  }, [user]);

  const fetchClinicInfo = async () => {
    try {
      const response = await axios.get(`${API}/clinic/profile`);
      setClinicInfo(response.data);
    } catch (error) {
      console.error('Error fetching clinic info:', error);
    }
  };

  const fetchAppointments = async () => {
    try {
      const response = await axios.get(`${API}/clinic/appointments`);
      setAppointments(response.data);
      
      // Process booked slots
      const booked = {};
      response.data.forEach(apt => {
        if (!booked[apt.appointment_date]) {
          booked[apt.appointment_date] = [];
        }
        booked[apt.appointment_date].push(apt.appointment_time);
      });
      setBookedSlots(booked);
    } catch (error) {
      console.error('Error fetching appointments:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailability = async () => {
    try {
      const response = await axios.get(`${API}/clinic/availability`);
      setAvailableSlots(response.data);
    } catch (error) {
      console.error('Error fetching availability:', error);
    }
  };

  const confirmAppointment = async (appointmentId) => {
    try {
      await axios.put(`${API}/clinic/appointments/${appointmentId}/confirm`);
      fetchAppointments();
    } catch (error) {
      console.error('Error confirming appointment:', error);
      alert('Failed to confirm appointment');
    }
  };

  const rescheduleAppointment = async (appointmentId, newDate, newTime) => {
    try {
      await axios.put(`${API}/clinic/appointments/${appointmentId}/reschedule`, {
        new_date: newDate,
        new_time: newTime
      });
      fetchAppointments();
      alert('Appointment rescheduled successfully');
    } catch (error) {
      console.error('Error rescheduling appointment:', error);
      alert('Failed to reschedule appointment');
    }
  };

  const sendConfirmationText = async (appointmentId) => {
    try {
      await axios.post(`${API}/clinic/appointments/${appointmentId}/send-confirmation`);
      alert('Confirmation text sent successfully');
    } catch (error) {
      console.error('Error sending confirmation:', error);
      alert('Failed to send confirmation text');
    }
  };

  const AppointmentCard = ({ appointment }) => {
    const [showReschedule, setShowReschedule] = useState(false);
    const [newDate, setNewDate] = useState('');
    const [newTime, setNewTime] = useState('');

    return (
      <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
        <div className="flex justify-between items-start mb-4">
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              {appointment.patient_name}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-gray-600"><strong>Date:</strong> {new Date(appointment.appointment_date).toLocaleDateString()}</p>
                <p className="text-gray-600"><strong>Time:</strong> {appointment.appointment_time}</p>
                <p className="text-gray-600"><strong>Phone:</strong> {appointment.patient_phone}</p>
              </div>
              <div>
                <p className="text-gray-600"><strong>Fee:</strong> ${appointment.consultation_fee}</p>
                <p className="text-gray-600"><strong>Status:</strong> 
                  <span className={`ml-2 px-2 py-1 rounded text-xs ${
                    appointment.status === 'confirmed' ? 'bg-green-100 text-green-800' :
                    appointment.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                    appointment.status === 'rescheduled' ? 'bg-blue-100 text-blue-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {appointment.status.charAt(0).toUpperCase() + appointment.status.slice(1)}
                  </span>
                </p>
                <p className="text-gray-600"><strong>Payment:</strong> 
                  <span className={`ml-2 px-2 py-1 rounded text-xs ${
                    appointment.payment_status === 'paid' ? 'bg-green-100 text-green-800' :
                    'bg-yellow-100 text-yellow-800'
                  }`}>
                    {appointment.payment_status.charAt(0).toUpperCase() + appointment.payment_status.slice(1)}
                  </span>
                </p>
              </div>
            </div>
            {appointment.symptoms && (
              <div className="mt-4">
                <p className="text-gray-600"><strong>Symptoms:</strong></p>
                <p className="text-gray-700 mt-1">{appointment.symptoms}</p>
              </div>
            )}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-wrap gap-2 mt-4">
          {appointment.status === 'pending' && (
            <button
              onClick={() => confirmAppointment(appointment.id)}
              className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors text-sm"
            >
              ✅ Confirm
            </button>
          )}
          
          <button
            onClick={() => setShowReschedule(!showReschedule)}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors text-sm"
          >
            📅 Reschedule
          </button>
          
          <button
            onClick={() => sendConfirmationText(appointment.id)}
            className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors text-sm"
          >
            📱 Send SMS
          </button>
        </div>

        {/* Reschedule Form */}
        {showReschedule && (
          <div className="mt-4 p-4 bg-gray-50 rounded-lg">
            <h4 className="font-semibold text-gray-900 mb-3">Reschedule Appointment</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">New Date</label>
                <input
                  type="date"
                  value={newDate}
                  onChange={(e) => setNewDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">New Time</label>
                <select
                  value={newTime}
                  onChange={(e) => setNewTime(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select Time</option>
                  {['09:00', '10:00', '11:00', '14:00', '15:00', '16:00'].map(time => (
                    <option key={time} value={time}>{time}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="flex gap-2 mt-4">
              <button
                onClick={() => rescheduleAppointment(appointment.id, newDate, newTime)}
                disabled={!newDate || !newTime}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:bg-gray-400 transition-colors text-sm"
              >
                Save Changes
              </button>
              <button
                onClick={() => setShowReschedule(false)}
                className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors text-sm"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    );
  };

  const CalendarView = () => (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
      <div className="lg:col-span-2">
        <Calendar 
          selectedDate={selectedDate}
          onDateSelect={setSelectedDate}
          availableSlots={availableSlots}
          bookedSlots={bookedSlots}
          mode="clinic"
        />
      </div>
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-gray-900">
          {selectedDate ? `Appointments for ${new Date(selectedDate).toLocaleDateString()}` : 'Select a date to view appointments'}
        </h3>
        {selectedDate && (
          <div className="space-y-3">
            {appointments
              .filter(apt => apt.appointment_date === selectedDate)
              .map(appointment => (
                <div key={appointment.id} className="bg-white p-4 rounded-lg border border-gray-200">
                  <div className="font-medium text-gray-900">{appointment.appointment_time}</div>
                  <div className="text-gray-600">{appointment.patient_name}</div>
                  <div className="text-sm text-gray-500">{appointment.symptoms?.substring(0, 50)}...</div>
                </div>
              ))}
          </div>
        )}
      </div>
    </div>
  );

  const LocationSettings = () => {
    const [location, setLocation] = useState({
      address: '',
      latitude: '',
      longitude: '',
      google_maps_url: ''
    });
    const [saving, setSaving] = useState(false);

    useEffect(() => {
      if (clinicInfo?.location) {
        setLocation(clinicInfo.location);
      }
    }, [clinicInfo]);

    const saveLocation = async () => {
      setSaving(true);
      try {
        await axios.put(`${API}/clinic/location`, location);
        alert('Location saved successfully');
        fetchClinicInfo();
      } catch (error) {
        console.error('Error saving location:', error);
        alert('Failed to save location');
      } finally {
        setSaving(false);
      }
    };

    return (
      <div className="bg-white rounded-lg shadow-lg p-6">
        <h3 className="text-xl font-semibold text-gray-900 mb-6">Clinic Location Settings</h3>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Full Address
            </label>
            <textarea
              value={location.address}
              onChange={(e) => setLocation({...location, address: e.target.value})}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Enter your clinic's complete address"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Latitude
              </label>
              <input
                type="text"
                value={location.latitude}
                onChange={(e) => setLocation({...location, latitude: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="e.g., 40.7128"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Longitude
              </label>
              <input
                type="text"
                value={location.longitude}
                onChange={(e) => setLocation({...location, longitude: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="e.g., -74.0060"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Google Maps URL
            </label>
            <input
              type="url"
              value={location.google_maps_url}
              onChange={(e) => setLocation({...location, google_maps_url: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="https://maps.google.com/..."
            />
            <p className="text-sm text-gray-500 mt-1">
              Share the Google Maps link to your clinic for accurate directions
            </p>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h4 className="font-medium text-blue-900 mb-2">📍 How to get your Google Maps URL:</h4>
            <ol className="text-sm text-blue-800 space-y-1">
              <li>1. Go to Google Maps and search for your clinic</li>
              <li>2. Click on your clinic's pin or listing</li>
              <li>3. Click the "Share" button</li>
              <li>4. Copy the link and paste it above</li>
            </ol>
          </div>

          <button
            onClick={saveLocation}
            disabled={saving}
            className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg hover:bg-blue-700 disabled:bg-gray-400 transition-colors font-medium"
          >
            {saving ? 'Saving...' : 'Save Location'}
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Clinic Dashboard</h1>
          <p className="text-gray-600">Manage appointments, schedules, and clinic settings</p>
        </div>

        {/* Tab Navigation */}
        <div className="flex border-b border-gray-200 mb-8">
          {[
            { id: 'appointments', label: 'Appointments', icon: '📅' },
            { id: 'calendar', label: 'Calendar View', icon: '🗓️' },
            { id: 'location', label: 'Location Settings', icon: '📍' }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center px-6 py-3 font-medium transition-colors ${
                activeTab === tab.id
                  ? 'text-blue-600 border-b-2 border-blue-600'
                  : 'text-gray-600 hover:text-blue-600'
              }`}
            >
              <span className="mr-2">{tab.icon}</span>
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading dashboard...</p>
          </div>
        ) : (
          <>
            {activeTab === 'appointments' && (
              <div className="space-y-6">
                <div className="flex justify-between items-center">
                  <h2 className="text-xl font-semibold text-gray-900">
                    Manage Appointments ({appointments.length})
                  </h2>
                </div>
                
                {appointments.length === 0 ? (
                  <div className="text-center py-12">
                    <div className="text-6xl mb-4">📅</div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">No appointments yet</h3>
                    <p className="text-gray-600">Appointments will appear here when users book with your clinic</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 gap-6">
                    {appointments.map((appointment) => (
                      <AppointmentCard key={appointment.id} appointment={appointment} />
                    ))}
                  </div>
                )}
              </div>
            )}

            {activeTab === 'calendar' && <CalendarView />}
            
            {activeTab === 'location' && <LocationSettings />}
          </>
        )}
      </div>
    </div>
  );
};

export default ClinicDashboard;